import { useEffect, useState } from 'react';
import axios from 'axios';
import Dashboard from './Dashboard.js';
import { useAuth } from '../../context/AuthContext.js';
import { formatDate } from '../../utilities.js';
import TwoFactorSetup from '../misc/TwoFactorSetup.js';
import './Home.css';

function Home() {
    const { isAuthenticated, userInfo } = useAuth();
    const role = userInfo?.role || '';

    const [students, setStudents] = useState([]);
    const [todayBirthdays, setTodayBirthdays] = useState([]);
    const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(null);

    const token = sessionStorage.getItem('jwtToken');
    axios.defaults.withCredentials = true;

    const canSeeDashboard = ['super_admin', 'locatie_admin', 'eigenaar', 'ouder', 'docent'].includes(role);
    const canSeeBirthdays = ['docent', 'super_admin', 'locatie_admin'].includes(role);

    useEffect(() => {
        const fetch2FAStatus = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/user/getprofile`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setTwoFactorEnabled(!!res.data.profile?.two_factor_enabled);
            } catch (err) {
                console.error('Kon 2FA status niet ophalen:', err);
                setTwoFactorEnabled(false);
            }
        };

        fetch2FAStatus();
    }, [token]);

    useEffect(() => {
        if (!['super_admin', 'locatie_admin', 'eigenaar', 'docent'].includes(role)) {
            setStudents([]);
            setTodayBirthdays([]);
            setUpcomingBirthdays([]);
            setIsLoading(false);
            return;
        }

        const fetchStudents = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/students/mystudents`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setStudents(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error(err);
                setStudents([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStudents();
    }, [role, token]);

    useEffect(() => {
        if (students.length === 0) {
            setTodayBirthdays([]);
            setUpcomingBirthdays([]);
            return;
        }

        const today = new Date();

        const todayList = students.filter((student) => {
            const birthDate = new Date(student.birthdate);
            return (
                birthDate.getDate() === today.getDate() &&
                birthDate.getMonth() === today.getMonth()
            );
        });

        const upcomingList = students
            .filter((student) => {
                const nextBirthday = new Date(student.birthdate);
                nextBirthday.setFullYear(today.getFullYear());

                if (nextBirthday < today) {
                    nextBirthday.setFullYear(today.getFullYear() + 1);
                }

                const diffInDays = (nextBirthday - today) / (1000 * 60 * 60 * 24);
                return diffInDays >= 0 && diffInDays <= 31;
            })
            .sort((a, b) => {
                const dateA = new Date(a.birthdate);
                const dateB = new Date(b.birthdate);
                return dateA.getMonth() - dateB.getMonth() || dateA.getDate() - dateB.getDate();
            });

        setTodayBirthdays(todayList);
        setUpcomingBirthdays(upcomingList);
    }, [students]);

    if (!isAuthenticated) return <p>You are not authenticated.</p>;

    return (
        <div className="home-content">
            {twoFactorEnabled !== null && (
                <TwoFactorSetup
                    twoFactorEnabled={twoFactorEnabled}
                    onEnabled={() => setTwoFactorEnabled(true)}
                    variant="banner"
                />
            )}

            {twoFactorEnabled !== false && (
                <h1 className="home-title">Welkom</h1>
            )}

            {canSeeDashboard && (
                <div
                    className={`home-grid ${role === 'ouder' || role === 'docent' ? 'home-grid-parent' : ''
                        }`}
                >
                    <div
                        className={`home-card ${role === 'ouder' || role === 'docent'
                                ? 'home-card-parent-dashboard'
                                : ''
                            }`}
                    >
                        <Dashboard />
                    </div>
                </div>
            )}



            {canSeeBirthdays && (
                <div className="birthday-grid">
                    <div className="home-card">
                        <h2>Vandaag jarig</h2>
                        {isLoading ? (
                            <p>Loading...</p>
                        ) : todayBirthdays.length > 0 ? (
                            <ul>
                                {todayBirthdays.map((student) => (
                                    <li key={student.id}>
                                        {student.firstname} {student.lastname} —{' '}
                                        {formatDate(new Date(student.birthdate))}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>Er is vandaag niemand jarig.</p>
                        )}
                    </div>

                    <div className="home-card">
                        <h2>Aankomende jarigen</h2>
                        {isLoading ? (
                            <p>Loading...</p>
                        ) : upcomingBirthdays.length > 0 ? (
                            <ul>
                                {upcomingBirthdays.map((student) => (
                                    <li key={student.id}>
                                        {student.firstname} {student.lastname} —{' '}
                                        {formatDate(new Date(student.birthdate))}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>Geen jarige leerlingen in de komende maand.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Home;